/**
 * Dispatch Intelligence — Unified Operations Control Center
 *
 * Every job has a next action. No dead jobs. No guessing.
 */

import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { buildDispatchQueue, filterDispatchQueue, type DispatchFilter, type DispatchQueueItem } from "@/data/dispatchIntelligence";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import { OrderingModal } from "@/components/OrderingModal";
import { toast } from "sonner";
import { JobService } from "@/services/job.service";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Zap, AlertTriangle, Clock, CheckCircle2, Search, ArrowRight,
  DollarSign, Shield, Wrench, Filter, TrendingUp, XCircle,
  CalendarDays, ClipboardCheck, Package, Target,
} from "lucide-react";
import type { Job } from "@/data/types";

const FILTERS: { key: DispatchFilter; label: string; icon: typeof Zap }[] = [
  { key: "all", label: "All Jobs", icon: Target },
  { key: "blocked", label: "Blocked", icon: XCircle },
  { key: "in_progress", label: "In Progress", icon: Wrench },
  { key: "payment_required", label: "Payment Due", icon: DollarSign },
  { key: "ready_to_schedule", label: "Ready to Schedule", icon: CalendarDays },
  { key: "qa_ready", label: "QA Ready", icon: ClipboardCheck },
  { key: "stalled", label: "Stalled", icon: Clock },
];

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  critical: { label: "CRITICAL", color: "text-destructive", bgColor: "bg-destructive/10 border-destructive/30" },
  high: { label: "HIGH", color: "text-warning", bgColor: "bg-warning/10 border-warning/30" },
  medium: { label: "MEDIUM", color: "text-primary", bgColor: "bg-primary/10 border-primary/30" },
  low: { label: "LOW", color: "text-muted-foreground", bgColor: "bg-muted/50 border-border" },
};

export default function DispatchIntelligence() {
  const { jobs, issues, installerWrapRecords, installFollowUpTasks, setJobs } = useAppData();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<DispatchFilter>("all");
  const [search, setSearch] = useState("");
  const [orderingJob, setOrderingJob] = useState<Job | null>(null);

  const fullQueue = useMemo(
    () => buildDispatchQueue(jobs, issues, installerWrapRecords, installFollowUpTasks),
    [jobs, issues, installerWrapRecords, installFollowUpTasks],
  );

  /**
   * Resolve a dispatch queue item's CTA into an ExecutableAction.
   * Modal mode triggers for material-ordering CTAs; everything else navigates.
   */
  const resolveDispatchAction = useCallback(
    (item: DispatchQueueItem): ExecutableAction => {
      const labelLower = item.nextActionCta.toLowerCase();
      const isOrderCta = labelLower.includes("order") && labelLower.includes("material");
      if (isOrderCta) {
        const job = jobs.find((j) => j.id === item.jobId);
        if (job) {
          return {
            label: item.nextActionCta,
            executionMode: "modal",
            openModal: () => setOrderingJob(job),
          };
        }
      }
      return { label: item.nextActionCta, executionMode: "navigate", route: item.nextActionRoute };
    },
    [jobs],
  );

  const handleDispatchCta = useCallback(
    async (item: DispatchQueueItem) => {
      const action = resolveDispatchAction(item);
      const handled = await runAction(action, { navigate });
      if (!handled) navigate(item.nextActionRoute);
    },
    [resolveDispatchAction, navigate],
  );

  const handleOrderingSubmit = useCallback(
    async (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => {
      const target = jobs.find((j) => j.id === data.jobId);
      if (!target) return;
      const patch: Partial<Job> = {
        materialReadiness: "Ordered" as any,
        planningStatus: "Ordered",
        selectedVendor: data.vendor,
        activityHistory: [
          ...target.activityHistory,
          {
            id: `dispatch-order-${Date.now()}`,
            date: new Date().toISOString(),
            action: `Materials ordered — ${data.vendor} (${data.orderType})`,
            user: "Dispatch",
            noteType: "ops" as const,
          },
        ],
      };
      const { data: persisted, error } = await JobService.update(target.id, patch);
      if (error || !persisted) {
        console.error("Failed to persist ordering update:", error);
        toast.error("Failed to place order");
        return;
      }
      setJobs((prev) => prev.map((j) => (j.id === persisted.id ? persisted : j)));
      toast.success("Order placed", { description: `${data.vendor} — ${data.orderType}` });
      setOrderingJob(null);
    },
    [jobs, setJobs],
  );

  const filtered = useMemo(() => {
    let q = filterDispatchQueue(fullQueue, activeFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      q = q.filter(item =>
        item.customerName.toLowerCase().includes(s) ||
        item.jobId.toLowerCase().includes(s) ||
        item.nextAction.toLowerCase().includes(s),
      );
    }
    return q;
  }, [fullQueue, activeFilter, search]);

  // KPI counts
  const kpis = useMemo(() => ({
    critical: fullQueue.filter(q => q.priority === "critical").length,
    blocked: fullQueue.filter(q => q.blockerReason !== null).length,
    paymentDue: fullQueue.filter(q =>
      q.state.payment === "deposit_pending" || q.state.payment === "payment_pending" || q.state.payment === "payment_deferred"
    ).length,
    stalled: fullQueue.filter(q => q.flags.includes("Stalled")).length,
    total: fullQueue.length,
  }), [fullQueue]);

  return (
    <div className="space-y-5 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Dispatch Intelligence
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {fullQueue.length} active jobs · Every job has a next action
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]">Live</Badge>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard label="Critical" value={kpis.critical} icon={AlertTriangle} color="text-destructive bg-destructive/10 border-destructive/20" />
        <KpiCard label="Blocked" value={kpis.blocked} icon={XCircle} color="text-warning bg-warning/10 border-warning/20" />
        <KpiCard label="Payment Due" value={kpis.paymentDue} icon={DollarSign} color="text-primary bg-primary/10 border-primary/20" />
        <KpiCard label="Stalled" value={kpis.stalled} icon={Clock} color="text-muted-foreground bg-muted/50 border-border" />
      </div>

      {/* Filters + Search */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all border ${
                activeFilter === f.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
              }`}
            >
              {f.label}
              {f.key !== "all" && (() => {
                const count = filterDispatchQueue(fullQueue, f.key).length;
                return count > 0 ? ` (${count})` : "";
              })()}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search jobs, customers, actions..."
            className="pl-8 h-8 text-xs"
          />
        </div>
      </div>

      {/* Queue */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center border rounded-lg bg-muted/20">
          <CheckCircle2 className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">
            {activeFilter === "all" ? "No active jobs" : `No jobs matching "${FILTERS.find(f => f.key === activeFilter)?.label}"`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => (
            <DispatchRow key={item.jobId} item={item} onCta={() => handleDispatchCta(item)} />
          ))}
        </div>
      )}

      {/* Ordering modal — opened via shim when CTA is "Order Materials" */}
      {orderingJob && (
        <OrderingModal
          open={!!orderingJob}
          onOpenChange={(open) => { if (!open) setOrderingJob(null); }}
          job={orderingJob}
          onSendToOrdering={handleOrderingSubmit}
        />
      )}
    </div>
  );
}

// ── KPI Card ──

function KpiCard({ label, value, icon: Icon, color }: {
  label: string; value: number; icon: typeof AlertTriangle; color: string;
}) {
  return (
    <div className={`rounded-xl border p-3 text-center ${color}`}>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <p className="text-2xl font-bold leading-none">{value}</p>
      <p className="text-[9px] font-semibold uppercase tracking-wider mt-0.5 opacity-70">{label}</p>
    </div>
  );
}

// ── Dispatch Row ──

function DispatchRow({ item, onCta }: { item: DispatchQueueItem; onCta: () => void }) {
  const priorityConfig = PRIORITY_CONFIG[item.priority];

  return (
    <Card className={`border-l-4 transition-all hover:shadow-sm ${
      item.priority === "critical" ? "border-l-destructive" :
      item.priority === "high" ? "border-l-warning" :
      item.priority === "medium" ? "border-l-primary" :
      "border-l-border"
    }`}>
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          {/* Priority Indicator */}
          <div className={`shrink-0 rounded-md px-1.5 py-0.5 border text-[8px] font-bold uppercase tracking-wider ${priorityConfig.bgColor} ${priorityConfig.color}`}>
            {priorityConfig.label}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
             <div className="flex items-center gap-2 flex-wrap">
               <span className="text-[10px] font-bold text-muted-foreground">{item.jobNumber}</span>
               <span className="text-sm font-semibold truncate">{item.customerName}</span>
             </div>

            <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Wrench className="h-3 w-3" /> {item.installLabel}
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" /> {item.paymentLabel}
              </span>
              <span>{item.currentStage}</span>
              <span className="truncate">{item.address}</span>
            </div>

            {/* Blocker */}
            {item.blockerReason && (
              <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-destructive">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                <span className="font-medium">{item.blockerReason}</span>
              </div>
            )}

            {/* Flags */}
            {item.flags.length > 0 && (
              <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                {item.flags.map(flag => (
                  <Badge key={flag} variant="outline" className="text-[8px] h-4 px-1.5 border-warning/40 text-warning">
                    {flag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Next Action + CTA */}
          <div className="shrink-0 text-right flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3 text-primary" />
              <span className="text-[10px] font-bold text-primary">{item.nextAction}</span>
            </div>
            <Button
              size="sm"
              variant={item.priority === "critical" ? "destructive" : "default"}
              className="h-7 text-[10px] px-3"
              onClick={onCta}
            >
              {item.nextActionCta}
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
